import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Mail, 
  Search, 
  Filter,
  Building,
  Users,
  Calendar,
  AlertTriangle,
  FileCheck,
  ChevronRight,
  Plus,
  X,
  Clock,
  Send,
  Eye,
  Trash2,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DocumentsJuridiques = () => {
  const { token, user } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  
  const [documentTypes, setDocumentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [history, setHistory] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    building_id: '',
    coproprietaire_id: '',
    ag_date: '',
    ag_time: '18:00',
    ag_location: '',
    ordre_du_jour: [''],
    proxy_deadline: '',
    montant_du: '',
    date_limite: '',
    charges_details: [],
    description_travaux: '',
    date_debut_prevue: '',
    duree_estimee: '',
    entreprise: ''
  });
  
  const [emailData, setEmailData] = useState({
    recipients: [''],
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [typesRes, categoriesRes, historyRes, buildingsRes, coproRes] = await Promise.all([
        axios.get(`${API}/documents-juridiques/types`, { headers }),
        axios.get(`${API}/documents-juridiques/categories`, { headers }),
        axios.get(`${API}/documents-juridiques/history`, { headers }),
        axios.get(`${API}/buildings`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/coproprietaires`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setDocumentTypes(typesRes.data);
      setCategories(categoriesRes.data);
      setHistory(historyRes.data);
      setBuildings(Array.isArray(buildingsRes.data) ? buildingsRes.data : []);
      setCoproprietaires(Array.isArray(coproRes.data) ? coproRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setFormData({
      ...formData,
      building_id: '',
      coproprietaire_id: ''
    });
    setShowModal(true);
  };

  const handleGenerateDocument = async () => {
    if (!selectedType) return;
    
    setGenerating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Prepare data based on document type
      const payload = {
        type: selectedType.type,
        building_id: formData.building_id,
        coproprietaire_id: formData.coproprietaire_id,
        ...formData
      };
      
      // Clean up ordre_du_jour
      if (payload.ordre_du_jour) {
        payload.ordre_du_jour = payload.ordre_du_jour.filter(item => item.trim() !== '');
      }
      
      const response = await axios.post(`${API}/documents-juridiques/generate`, payload, { headers });
      
      toast.success('Document généré avec succès');
      
      // Download the document
      window.open(`${API}/documents-juridiques/${response.data.id}/download`, '_blank');
      
      setShowModal(false);
      fetchData(); // Refresh history
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (docId) => {
    window.open(`${API}/documents-juridiques/${docId}/download`, '_blank');
  };

  const handleSendEmail = async () => {
    if (!selectedDocument) return;
    
    const validRecipients = emailData.recipients.filter(r => r.trim() !== '');
    if (validRecipients.length === 0) {
      toast.error('Veuillez ajouter au moins un destinataire');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/documents-juridiques/${selectedDocument.id}/send-email`, {
        recipients: validRecipients,
        subject: emailData.subject,
        message: emailData.message
      }, { headers });
      
      toast.success('Document envoyé par email');
      setShowEmailModal(false);
      setSelectedDocument(null);
      fetchData();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/documents-juridiques/${docId}`, { headers });
      toast.success('Document supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEmailModal = (doc) => {
    setSelectedDocument(doc);
    setEmailData({
      recipients: [''],
      subject: `${doc.type_name} - ${doc.building_name || 'Copropriété'}`,
      message: `Veuillez trouver ci-joint le document: ${doc.type_name}`
    });
    setShowEmailModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      assemblee: <Calendar className="text-blue-500" size={20} />,
      recouvrement: <AlertTriangle className="text-amber-500" size={20} />,
      attestation: <FileCheck className="text-emerald-500" size={20} />,
      vente: <Building className="text-purple-500" size={20} />,
      administratif: <FileText className="text-gray-500" size={20} />,
      travaux: <Building className="text-orange-500" size={20} />
    };
    return icons[category] || <FileText className="text-gray-500" size={20} />;
  };

  const filteredTypes = selectedCategory 
    ? documentTypes.filter(t => t.category === selectedCategory)
    : documentTypes;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-juridiques">
      {isMobile && <MobileHeader title="Documents Juridiques" onMenuClick={onMenuClick} />}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents Juridiques</h1>
          <p className="text-gray-500 mt-1">Générez vos documents de copropriété</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'generate'
              ? 'border-[#00D3A8] text-[#00D3A8]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-generate"
        >
          <Plus size={16} className="inline mr-2" />
          Nouveau document
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-[#00D3A8] text-[#00D3A8]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-history"
        >
          <Clock size={16} className="inline mr-2" />
          Historique ({history.length})
        </button>
      </div>

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="space-y-6">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === ''
                  ? 'bg-[#00D3A8] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-[#00D3A8] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Document Types Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTypes.map((docType) => (
              <div
                key={docType.type}
                onClick={() => handleSelectType(docType)}
                className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md hover:border-[#00D3A8]/30 transition-all"
                data-testid={`doc-type-${docType.type}`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-gray-50 rounded-lg">
                    {getCategoryIcon(docType.category)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{docType.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{docType.description}</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {history.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText size={48} className="mx-auto mb-3 text-gray-300" />
              <p>Aucun document généré</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {history.map((doc) => (
                <div key={doc.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        {getCategoryIcon(doc.category)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.type_name}</h4>
                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                          {doc.building_name && (
                            <span className="flex items-center gap-1">
                              <Building size={14} />
                              {doc.building_name}
                            </span>
                          )}
                          {doc.coproprietaire_name && (
                            <span className="flex items-center gap-1">
                              <Users size={14} />
                              {doc.coproprietaire_name}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Généré le {formatDate(doc.generated_at)} par {doc.generated_by_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.sent_at && (
                        <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-600 rounded-full">
                          Envoyé
                        </span>
                      )}
                      <button
                        onClick={() => handleDownload(doc.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Télécharger"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => openEmailModal(doc)}
                        className="p-2 text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
                        title="Envoyer par email"
                      >
                        <Mail size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Generate Modal */}
      {showModal && selectedType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedType.name}</h2>
                <p className="text-sm text-gray-500">{selectedType.description}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Immeuble <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.building_id}
                  onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                  data-testid="select-building"
                >
                  <option value="">Sélectionner un immeuble</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>{b.nom} - {b.adresse}</option>
                  ))}
                </select>
              </div>

              {/* Coproprietaire Selection (for specific types) */}
              {['mise_en_demeure', 'lettre_relance', 'attestation_non_gage', 'etat_date', 'courrier_bienvenue', 'demande_autorisation_travaux'].includes(selectedType.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Copropriétaire <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.coproprietaire_id}
                    onChange={(e) => setFormData({ ...formData, coproprietaire_id: e.target.value })}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    data-testid="select-coproprietaire"
                  >
                    <option value="">Sélectionner un copropriétaire</option>
                    {coproprietaires
                      .filter(c => !formData.building_id || c.building_ids?.includes(formData.building_id))
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                      ))}
                  </select>
                </div>
              )}

              {/* Convocation AG specific fields */}
              {selectedType.type === 'convocation_ag' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date AG</label>
                      <input
                        type="date"
                        value={formData.ag_date}
                        onChange={(e) => setFormData({ ...formData, ag_date: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Heure AG</label>
                      <input
                        type="time"
                        value={formData.ag_time}
                        onChange={(e) => setFormData({ ...formData, ag_time: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
                    <input
                      type="text"
                      value={formData.ag_location}
                      onChange={(e) => setFormData({ ...formData, ag_location: e.target.value })}
                      placeholder="Adresse de l'assemblée"
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordre du jour</label>
                    {formData.ordre_du_jour.map((item, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const newItems = [...formData.ordre_du_jour];
                            newItems[index] = e.target.value;
                            setFormData({ ...formData, ordre_du_jour: newItems });
                          }}
                          placeholder={`Point ${index + 1}`}
                          className="flex-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                        />
                        {index > 0 && (
                          <button
                            onClick={() => {
                              const newItems = formData.ordre_du_jour.filter((_, i) => i !== index);
                              setFormData({ ...formData, ordre_du_jour: newItems });
                            }}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => setFormData({ ...formData, ordre_du_jour: [...formData.ordre_du_jour, ''] })}
                      className="text-sm text-[#00D3A8] hover:underline"
                    >
                      + Ajouter un point
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date limite pouvoirs</label>
                    <input
                      type="date"
                      value={formData.proxy_deadline}
                      onChange={(e) => setFormData({ ...formData, proxy_deadline: e.target.value })}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    />
                  </div>
                </>
              )}

              {/* Mise en demeure / Relance fields */}
              {['mise_en_demeure', 'lettre_relance'].includes(selectedType.type) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant dû (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_du}
                      onChange={(e) => setFormData({ ...formData, montant_du: parseFloat(e.target.value) || 0 })}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    />
                  </div>
                  {selectedType.type === 'mise_en_demeure' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date limite de paiement</label>
                      <input
                        type="date"
                        value={formData.date_limite}
                        onChange={(e) => setFormData({ ...formData, date_limite: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Travaux fields */}
              {selectedType.type === 'demande_autorisation_travaux' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description des travaux</label>
                    <textarea
                      value={formData.description_travaux}
                      onChange={(e) => setFormData({ ...formData, description_travaux: e.target.value })}
                      rows={4}
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      placeholder="Décrivez les travaux envisagés..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de début prévue</label>
                      <input
                        type="date"
                        value={formData.date_debut_prevue}
                        onChange={(e) => setFormData({ ...formData, date_debut_prevue: e.target.value })}
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Durée estimée</label>
                      <input
                        type="text"
                        value={formData.duree_estimee}
                        onChange={(e) => setFormData({ ...formData, duree_estimee: e.target.value })}
                        placeholder="Ex: 2 semaines"
                        className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise intervenante</label>
                    <input
                      type="text"
                      value={formData.entreprise}
                      onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                      placeholder="Nom de l'entreprise"
                      className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleGenerateDocument}
                disabled={generating || !formData.building_id}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B894] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="btn-generate"
              >
                {generating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FileText size={18} />
                    Générer le document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Envoyer par email</h2>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destinataires</label>
                {emailData.recipients.map((email, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const newRecipients = [...emailData.recipients];
                        newRecipients[index] = e.target.value;
                        setEmailData({ ...emailData, recipients: newRecipients });
                      }}
                      placeholder="email@exemple.com"
                      className="flex-1 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                    />
                    {index > 0 && (
                      <button
                        onClick={() => {
                          const newRecipients = emailData.recipients.filter((_, i) => i !== index);
                          setEmailData({ ...emailData, recipients: newRecipients });
                        }}
                        className="p-3 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setEmailData({ ...emailData, recipients: [...emailData.recipients, ''] })}
                  className="text-sm text-[#00D3A8] hover:underline"
                >
                  + Ajouter un destinataire
                </button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objet</label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  rows={4}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/50"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendEmail}
                className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B894] transition-colors flex items-center gap-2"
              >
                <Send size={18} />
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsJuridiques;
